"use client"

import { toast } from "sonner"
import { useSettingsStore } from "@/lib/settings-store"

export interface DownloadItem {
  url: string
  filename: string
}

function proxyUrl(url: string, filename: string) {
  return `/api/proxy/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`
}

// Minimal ambient type for the File System Access API — not yet in all TS lib.dom versions.
interface SaveFilePickerWindow extends Window {
  showSaveFilePicker?: (options?: { suggestedName?: string }) => Promise<{
    createWritable: () => Promise<{ write: (data: Blob) => Promise<void>; close: () => Promise<void> }>
  }>
}

// Saves a blob to disk, respecting the user's "ask where to save" preference (shared by every
// download surface in the app — booru posts, script content, script + funscript zips). When the
// setting asks for a prompt and the browser supports the File System Access API (Chromium only),
// shows a native save dialog; otherwise falls back to a normal silent browser download, which is
// the only option available on Safari/Firefox regardless of the setting.
async function saveBlob(blob: Blob, filename: string): Promise<void> {
  const autoDownloadWithoutDialog = useSettingsStore.getState().autoDownloadWithoutDialog
  const w = window as SaveFilePickerWindow

  if (!autoDownloadWithoutDialog && typeof w.showSaveFilePicker === "function") {
    try {
      const handle = await w.showSaveFilePicker({ suggestedName: filename })
      const writable = await handle.createWritable()
      await writable.write(blob)
      await writable.close()
      return
    } catch (err: any) {
      // User cancelled the picker — respect that, don't also silently download.
      if (err?.name === "AbortError") return
      // Any other failure (e.g. picker threw for an unrelated reason) — fall through below.
    }
  }

  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = objectUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(objectUrl)
}

// Toast text lives in a plain function, not a component — useTranslations()
// is a hook and can't be called here. Every real caller IS a component, so
// they pass their own `useTranslations("contentDownload")` in; anything
// that doesn't (or a caller several layers removed, like the Goonfolio
// bulk-download helpers below) falls back to English via DEFAULT_MESSAGES
// rather than failing to compile or throwing at runtime.
export type ContentDownloadKey = "noDownloadableContent" | "downloadFailed" | "scriptExcluded" | "downloadComplete"
export type ContentDownloadT = (key: ContentDownloadKey) => string
const DEFAULT_MESSAGES: Record<ContentDownloadKey, string> = {
  noDownloadableContent: "No downloadable content found.",
  downloadFailed: "Download failed. Please try again.",
  scriptExcluded: "Couldn't include the script file — downloading content only.",
  downloadComplete: "Download complete!",
}

export interface DownloadContentOptions {
  /** Extra file to bundle alongside the content (e.g. a .funscript) — always zips when present. */
  extraFile?: DownloadItem
  onProgress?: (percent: number) => void
  t?: ContentDownloadT
}

/**
 * Downloads one or more content items. A single item downloads directly; multiple items (a
 * doujin/gallery/erome-style set — could be 100+ images) are automatically zipped into one folder
 * so the user never has to download them one at a time. Shared by the booru modal, the script
 * player, and the library "Download Content"/"Download All" menu actions so this logic — and the
 * save-location preference — only exists in one place.
 */
export async function downloadContentItems(
  items: DownloadItem[],
  zipName: string,
  options: DownloadContentOptions = {},
): Promise<boolean> {
  const { extraFile, onProgress, t = (k: ContentDownloadKey) => DEFAULT_MESSAGES[k] } = options

  if (items.length === 0 && !extraFile) {
    toast.error(t("noDownloadableContent"))
    return false
  }

  // Single file, nothing extra to bundle — direct download, no zip wrapper.
  if (items.length === 1 && !extraFile) {
    const item = items[0]
    try {
      onProgress?.(0)
      const res = await fetch(proxyUrl(item.url, item.filename))
      if (!res.ok) throw new Error(`Fetch failed: ${res.status}`)
      await saveBlob(await res.blob(), item.filename)
      onProgress?.(100)
      return true
    } catch {
      toast.error(t("downloadFailed"))
      return false
    }
  }

  // Multiple files, or a single file plus an extra (e.g. funscript) to bundle — zip everything.
  try {
    const JSZip = (await import("jszip")).default
    const zip = new JSZip()
    const total = items.length + (extraFile ? 1 : 0)
    let done = 0

    for (const item of items) {
      try {
        const res = await fetch(proxyUrl(item.url, item.filename))
        if (res.ok) zip.file(item.filename, await res.blob())
      } catch { /* skip failed file, continue with the rest */ }
      done++
      onProgress?.(Math.round((done / total) * 90))
    }

    if (extraFile) {
      try {
        const res = await fetch(extraFile.url)
        if (res.ok) zip.file(extraFile.filename, await res.blob())
        else toast.error(t("scriptExcluded"))
      } catch {
        toast.error(t("scriptExcluded"))
      }
      done++
      onProgress?.(Math.round((done / total) * 90))
    }

    const blob = await zip.generateAsync({ type: "blob" }, (meta) => {
      onProgress?.(90 + Math.round(meta.percent * 0.1))
    })
    await saveBlob(blob, `${zipName}.zip`)
    toast.success(t("downloadComplete"))
    return true
  } catch {
    toast.error(t("downloadFailed"))
    return false
  }
}

export function extFromUrl(url: string): string {
  const clean = (url.split("?")[0] ?? url).split("#")[0] ?? url
  const dot = clean.lastIndexOf(".")
  const slash = clean.lastIndexOf("/")
  return dot > slash ? clean.slice(dot + 1).toLowerCase() || "bin" : "bin"
}
